import { type ApolloCache, type Reference, useApolloClient } from '@apollo/client'

import { useProjectId } from '@/hooks/useProjectId.ts'
import { useMutateStory } from '@/requests/mutations/useMutateStory.ts'
import { useQueryStory } from '@/requests/queries/useQueryStory.ts'

export const useMutateStoryLabels = (storyId: string) => {
  const { mutate } = useMutateStory(storyId)
  const projectId = useProjectId()
  const { story } = useQueryStory(storyId)
  const { cache } = useApolloClient()

  if (!story) throw new Error(`Story with id ${storyId} not found`)

  return {
    add: async (label: Label) => {
      const labels = [...(story?.labels ?? []), label]

      return mutate(
        { labels },
        {
          update: (cache, { data: { executeCommand } }) => {
            const [updatedStory] = executeCommand.data
            const temporaryLabel: Label = { ...label, __typename: 'Label' }

            const existingStoryLabel = updatedStory.labels.find((l: Label) => l.name === label.name)
            if (!existingStoryLabel?.__typename) {
              addIfNotExists(cache, cache.identify({ __typename: 'Project', id: projectId }), temporaryLabel)
              addIfNotExists(cache, cache.identify({ __typename: 'Story', id: storyId }), temporaryLabel)
              cache.modify({
                id: cache.identify({ __typename: 'Label', id: label.id }),
                fields: {
                  count: (existingCount: number) => existingCount + 1,
                },
              })
            } else {
              const newLabel = updatedStory.labels.find((l: Label) => l.name === label.name)
              addIfNotExists(cache, cache.identify({ __typename: 'Project', id: projectId }), newLabel)
              addIfNotExists(cache, cache.identify({ __typename: 'Story', id: storyId }), newLabel)
            }
          },
        },
      )
    },

    remove: (id: string) => {
      const labels = (story?.labels ?? []).filter((label: Label) => label.id !== id)

      cache.modify({
        id: cache.identify({ __typename: 'Label', id: id }),
        fields: {
          count: (existingCount) => (existingCount - 1 >= 0 ? existingCount - 1 : 0),
        },
      })

      return mutate({ labels })
    },
  }
}

// biome-ignore lint/suspicious/noExplicitAny:
const addIfNotExists = (cache: ApolloCache<any>, identifier: string, label: Label) => {
  cache.modify({
    id: identifier,
    fields: {
      // biome-ignore lint/style/useDefaultParameterLast: This is a GraphQL cache configuration
      labels(existingLabels = [], { toReference, readField }) {
        if (existingLabels.some((l: Reference) => readField('name', l) === label.name)) return existingLabels

        return [...existingLabels, toReference(label, true)]
      },
    },
  })
}
